/**
 * The space's files, on its overview: its own documents with their version
 * history, then the files its chats made or were given.
 *
 * - **Documents** are the space's real-time documents (`spaces/realtime`):
 *   the Board, the live page every space has, edited by the team and the
 *   agent at once, and any page or sheet the space gains. Each row shows its
 *   latest change and unfolds its saved versions (who, when, a label when
 *   someone named it). A document opens in place (`?doc=board`) with a way
 *   back to the overview.
 * - **Upload** (the team only) takes a Markdown file, an .xlsx or a .csv,
 *   by the button or by dropping it on the list: Markdown becomes a page in
 *   the Board's live editor, a spreadsheet a live sheet (`sheet/`). It opens
 *   at once. An open document downloads as .md or .xlsx, saves a named
 *   version and shows its history.
 * - **From chats** are the files in the workspaces of this space's chats, as
 *   the person's workspace catalog lists them (only conversations the person
 *   may open), latest version only. A row opens its chat.
 *
 * @module components/spaces/SpaceFiles
 */
import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import { ThreadId, WORKSPACE_CATALOG_MAX_THREAD_LIMIT, type EnvironmentId } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  HistoryIcon,
  ImageIcon,
  LayoutDashboardIcon,
  PresentationIcon,
  UploadIcon,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { workspaceCatalog } from "~/state/projects";
import { useEnvironmentQuery } from "~/state/query";
import {
  SPACE_UPLOAD_ACCEPT,
  refreshSpaceActivity,
  refreshSpaceDocuments,
  saveSpaceDocumentVersionRequest,
  spaceDocumentDownloadUrl,
  uploadSpaceFileRequest,
  type SpaceDocumentSummary,
  type SpaceDocumentVersion,
  type SpaceListView,
} from "../../state/spaces";
import type { SpaceDocumentProvider } from "../../spaces/realtime/SpaceDocumentProvider";
import { openSpaceDocumentProvider } from "../../spaces/realtime/useSpaceDocument";
import { buildThreadRouteParams } from "../../threadRoutes";
import { compactAge } from "../home/homeLogic";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { SpaceBoard } from "./board/SpaceBoard";
import { SpaceBoardLive } from "./board/SpaceBoardLive";
import { SpaceSheetLive } from "./sheet/SpaceSheetLive";
import { fileSizeLabel, spaceChatFiles, spaceDocumentRows, type SpaceChatFile, type SpaceDocumentRow } from "./SpacePage.logic";

const ROW_CLASS =
  "group flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-start outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring";

function FileMark({ icon: Icon, tone = "muted" }: { readonly icon: LucideIcon; readonly tone?: "muted" | "doc" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-lg",
        tone === "doc" ? "bg-primary/10 text-primary" : "border border-border/70 bg-background text-muted-foreground",
      )}
    >
      <Icon className="size-4" />
    </span>
  );
}

const iconForPath = (path: string): LucideIcon => {
  const extension = path.split(".").pop()?.toLowerCase() ?? "";
  if (["xlsx", "xls", "csv", "tsv"].includes(extension)) return FileSpreadsheetIcon;
  if (["pptx", "ppt", "key"].includes(extension)) return PresentationIcon;
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(extension)) return ImageIcon;
  if (["md", "txt", "pdf", "docx", "doc", "html", "json"].includes(extension)) return FileTextIcon;
  return FileIcon;
};

const DOCUMENT_ICON: Readonly<Record<string, LucideIcon>> = {
  board: LayoutDashboardIcon,
  sheet: FileSpreadsheetIcon,
  page: FileTextIcon,
};

function SubHeading({ title, count }: { readonly title: string; readonly count?: number }) {
  return (
    <div className="mb-1 flex items-baseline gap-2 px-3 pt-1">
      <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
      {count !== undefined ? <span className="text-xs tabular-nums text-muted-foreground/70">{count}</span> : null}
    </div>
  );
}

const ago = (iso: string, now: number) => {
  const age = compactAge(iso, now);
  return age === "now" ? "just now" : `${age} ago`;
};

/** Who saved a version: their email, or "Amoreg" for the agent and the system. */
const byline = (createdBy: string | null) => {
  if (createdBy === null) return "Amoreg";
  const email = createdBy.startsWith("workos:") ? createdBy.slice("workos:".length) : createdBy;
  return email.includes("@") ? email : "Amoreg";
};

/* ------------------------------------------------------------------ */
/* Chat files                                                          */
/* ------------------------------------------------------------------ */

export interface SpaceChatFilesView {
  /** False when the space has no chats, so nothing was asked. */
  readonly enabled: boolean;
  readonly files: ReadonlyArray<SpaceChatFile>;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
}

/** The files of the space's chats, from the person's workspace catalog. */
export function useSpaceChatFiles(
  environmentId: EnvironmentId | null,
  hostOrganizationId: string | null,
  spaceThreadIds: ReadonlySet<string>,
): SpaceChatFilesView {
  const enabled = environmentId !== null && hostOrganizationId !== null && spaceThreadIds.size > 0;
  const catalog = useEnvironmentQuery(
    enabled
      ? workspaceCatalog({
          environmentId,
          input: { organizationId: hostOrganizationId, threadLimit: WORKSPACE_CATALOG_MAX_THREAD_LIMIT, fileLimit: 500 },
        })
      : null,
  );
  const files = useMemo(
    () => (catalog.data ? spaceChatFiles(catalog.data.threads, spaceThreadIds) : []),
    [catalog.data, spaceThreadIds],
  );
  return {
    enabled,
    files,
    isPending: enabled && catalog.isPending && catalog.data === null,
    error: catalog.data === null ? catalog.error : null,
    refresh: catalog.refresh,
  };
}

function ChatFileRow({
  file,
  environmentId,
  hostOrganizationId,
  now,
}: {
  readonly file: SpaceChatFile;
  readonly environmentId: EnvironmentId;
  readonly hostOrganizationId: string;
  readonly now: number;
}) {
  return (
    <li>
      <Link
        to="/$org/$threadId"
        params={buildThreadRouteParams(scopeThreadRef(environmentId, ThreadId.make(file.threadId)), hostOrganizationId)}
        className={ROW_CLASS}
        data-space-chat-file={file.path}
        title={file.path}
      >
        <FileMark icon={iconForPath(file.path)} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-foreground">{file.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            In {file.threadTitle} · {fileSizeLabel(file.size)}
          </span>
        </span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{ago(file.modifiedAt, now)}</span>
        <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground/50" />
      </Link>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Documents                                                           */
/* ------------------------------------------------------------------ */

/** A document's saved versions, newest first: number, label, who, when. */
function VersionList({
  docId,
  versions,
  now,
  className,
}: {
  readonly docId: string;
  readonly versions: ReadonlyArray<SpaceDocumentVersion>;
  readonly now: number;
  readonly className?: string;
}) {
  return (
    <ol className={cn("border-s border-border/70 ps-3", className)} data-space-doc-versions={docId}>
      {versions.map((version) => (
        <li key={version.version} className="flex items-baseline gap-2 py-1 text-xs">
          <span className="w-8 shrink-0 tabular-nums text-muted-foreground">v{version.version}</span>
          <span className="min-w-0 flex-1 truncate text-foreground/90">
            {version.label ?? "Saved automatically"}
            <span className="text-muted-foreground"> · {byline(version.createdBy)}</span>
          </span>
          <span className="shrink-0 tabular-nums text-muted-foreground">{ago(version.createdAt, now)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Upload a file into the space; the result opens. Errors stay in the server's words. */
function useSpaceUpload(spaceId: string, onUploaded: (docId: string) => void) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = async (file: File) => {
    setBusy(file.name);
    setError(null);
    const outcome = await uploadSpaceFileRequest(spaceId, file);
    setBusy(null);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    refreshSpaceDocuments(spaceId);
    refreshSpaceActivity(spaceId);
    onUploaded(outcome.document.docId);
  };
  return { busy, error, upload, clearError: () => setError(null) };
}

function DocumentRow({
  row,
  now,
  onOpen,
}: {
  readonly row: SpaceDocumentRow;
  readonly now: number;
  readonly onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const versions = row.versions;
  return (
    <li data-space-doc-row={row.docId}>
      <div className="flex items-center gap-1">
        <button type="button" className={cn(ROW_CLASS, "flex-1")} onClick={onOpen} data-space-open-doc={row.docId}>
          <FileMark icon={DOCUMENT_ICON[row.kind] ?? FileTextIcon} tone="doc" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-foreground">{row.name}</span>
            <span className="block truncate text-xs text-muted-foreground">{row.summary}</span>
          </span>
          {row.updatedAt ? (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">Edited {ago(row.updatedAt, now)}</span>
          ) : null}
        </button>
        {versions.length > 0 ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? `Hide the versions of ${row.name}` : `Show the versions of ${row.name}`}
            onClick={() => setOpen((shown) => !shown)}
            className="flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-md px-2 text-xs text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            data-space-doc-versions-toggle={row.docId}
          >
            {versions.length} {versions.length === 1 ? "version" : "versions"}
            <ChevronDownIcon aria-hidden className={cn("size-3.5 transition-transform", open && "rotate-180")} />
          </button>
        ) : null}
      </div>
      {open ? (
        <VersionList
          docId={row.docId}
          versions={versions}
          now={now}
          className="mb-2 ms-[3.25rem]"
        />
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* The section, and a document open in place                           */
/* ------------------------------------------------------------------ */

export function SpaceFilesList({
  spaceId,
  canUpload,
  documents,
  chatFiles,
  environmentId,
  hostOrganizationId,
  now,
  onOpenDoc,
  limit,
}: {
  readonly spaceId: string;
  /** The space's team may add files; guests may not. */
  readonly canUpload: boolean;
  readonly documents: SpaceListView<SpaceDocumentSummary>;
  readonly chatFiles: SpaceChatFilesView;
  readonly environmentId: EnvironmentId | null;
  readonly hostOrganizationId: string;
  readonly now: number;
  readonly onOpenDoc: (doc: string) => void;
  /** Chat files shown before "Show all". */
  readonly limit: number;
}) {
  const [allFiles, setAllFiles] = useState(false);
  const [dropping, setDropping] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);
  const uploads = useSpaceUpload(spaceId, onOpenDoc);
  const rows = useMemo(() => spaceDocumentRows(documents.items), [documents.items]);
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!canUpload) return;
    event.preventDefault();
    setDropping(false);
    const file = event.dataTransfer.files[0];
    if (file) void uploads.upload(file);
  };
  const shownFiles = allFiles ? chatFiles.files : chatFiles.files.slice(0, limit);
  let fromChats: ReactNode;
  if (!chatFiles.enabled) {
    fromChats = <p className="px-3 py-2 text-[13px] text-muted-foreground">Files the space's chats make or are given show here.</p>;
  } else if (chatFiles.isPending) {
    fromChats = (
      <div className="px-3">
        <ListSkeleton rows={2} />
      </div>
    );
  } else if (chatFiles.error !== null) {
    fromChats = (
      <p className="px-3 py-2 text-[13px] text-muted-foreground">
        The chats' files could not be listed.{" "}
        <button type="button" className="cursor-pointer underline-offset-2 hover:underline" onClick={chatFiles.refresh}>
          Try again
        </button>
      </p>
    );
  } else if (chatFiles.files.length === 0) {
    fromChats = <p className="px-3 py-2 text-[13px] text-muted-foreground">No files in this space's chats yet.</p>;
  } else {
    fromChats = (
      <>
        <ul>
          {environmentId !== null
            ? shownFiles.map((file) => (
                <ChatFileRow
                  key={`${file.threadId}:${file.path}`}
                  file={file}
                  environmentId={environmentId}
                  hostOrganizationId={hostOrganizationId}
                  now={now}
                />
              ))
            : null}
        </ul>
        {chatFiles.files.length > limit ? (
          <button
            type="button"
            onClick={() => setAllFiles((shown) => !shown)}
            className="mt-1 cursor-pointer px-3 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {allFiles ? "Show fewer" : `Show all ${chatFiles.files.length} files`}
          </button>
        ) : null}
      </>
    );
  }
  return (
    <div className="-mx-3 space-y-4" data-space-files="">
      <div
        data-space-files-documents=""
        className={cn("rounded-xl transition-colors", dropping && "bg-primary/5 outline-2 outline-dashed outline-primary/40")}
        onDragOver={(event) => {
          if (!canUpload || !event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={onDrop}
      >
        <div className="flex items-center justify-between gap-2 pe-2">
          <SubHeading title="Documents" count={rows.length} />
          {canUpload ? (
            <>
              <button
                type="button"
                onClick={() => picker.current?.click()}
                disabled={uploads.busy !== null}
                className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60"
                data-space-upload=""
              >
                <UploadIcon aria-hidden className="size-3.5" />
                {uploads.busy !== null ? `Uploading ${uploads.busy}` : "Upload"}
              </button>
              <input
                ref={picker}
                type="file"
                accept={SPACE_UPLOAD_ACCEPT}
                className="hidden"
                data-space-upload-input=""
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void uploads.upload(file);
                }}
              />
            </>
          ) : null}
        </div>
        {uploads.error !== null ? (
          <p className="px-3 pb-1 text-xs text-destructive" role="alert" data-space-upload-error="">
            {uploads.error}{" "}
            <button type="button" className="cursor-pointer underline-offset-2 hover:underline" onClick={uploads.clearError}>
              Dismiss
            </button>
          </p>
        ) : null}
        <ul>
          {rows.map((row) => (
            <DocumentRow key={row.docId} row={row} now={now} onOpen={() => onOpenDoc(row.docId)} />
          ))}
        </ul>
        {canUpload ? (
          <p className="px-3 pt-1 text-xs text-muted-foreground/80">
            Drop a Markdown file, an .xlsx or a .csv here to edit it together.
          </p>
        ) : null}
      </div>
      <div data-space-files-chats="">
        <SubHeading
          title="From chats"
          {...(chatFiles.enabled && chatFiles.error === null && !chatFiles.isPending ? { count: chatFiles.files.length } : {})}
        />
        {fromChats}
      </div>
    </div>
  );
}

const HEADER_ACTION =
  "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60";

/** Why the server's copy is not yet what this screen shows. */
function unsyncedNote(provider: SpaceDocumentProvider): string {
  if (provider.hasRefusedEdits) {
    return "Some edits here were refused by the server. Reload the page to see the saved copy.";
  }
  return provider.status === "synced"
    ? "Your latest edits have not reached the server yet. Try again in a moment."
    : "Not connected. Your edits are kept here; try again once you are back online.";
}

/**
 * Download the open document as the server stores it, once the server holds
 * every edit on screen; otherwise say why not instead of a stale file.
 */
function DownloadDocument({
  spaceId,
  docId,
  label,
}: {
  readonly spaceId: string;
  readonly docId: string;
  readonly label: string;
}) {
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const url = spaceDocumentDownloadUrl(spaceId, docId);
  const download = async () => {
    setNote(null);
    const provider = openSpaceDocumentProvider(spaceId, docId);
    if (provider !== null) {
      setBusy(true);
      const ready = await provider.whenAcknowledged();
      setBusy(false);
      if (!ready) {
        setNote(unsyncedNote(provider));
        return;
      }
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    link.click();
  };
  return (
    <>
      {note !== null ? (
        <span className="text-xs text-muted-foreground" role="status" data-space-download-note="">
          {note}
        </span>
      ) : null}
      <button
        type="button"
        className={HEADER_ACTION}
        disabled={busy}
        onClick={() => void download()}
        data-space-download=""
      >
        <DownloadIcon aria-hidden className="size-3.5" />
        {label}
      </button>
    </>
  );
}

/** Save a named version of the open document: an inline name, then the server's answer. */
function SaveVersion({ spaceId, docId }: { readonly spaceId: string; readonly docId: string }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setNote(null);
    // The version is the server's copy: wait until it holds every edit on
    // screen, and refuse while offline rather than checkpoint an older one.
    const provider = openSpaceDocumentProvider(spaceId, docId);
    if (provider !== null && !(await provider.whenAcknowledged())) {
      setBusy(false);
      setNote(unsyncedNote(provider));
      return;
    }
    const outcome = await saveSpaceDocumentVersionRequest(spaceId, docId, label.trim() || null);
    setBusy(false);
    if (!outcome.ok) {
      setNote(outcome.message);
      return;
    }
    setNote(`Saved as version ${outcome.version.version}`);
    setLabel("");
    setOpen(false);
    refreshSpaceDocuments(spaceId);
    refreshSpaceActivity(spaceId);
  };
  if (!open) {
    return (
      <>
        {note !== null ? (
          <span className="text-xs text-muted-foreground" role="status" data-space-version-note="">
            {note}
          </span>
        ) : null}
        <button
          type="button"
          className={HEADER_ACTION}
          onClick={() => {
            setNote(null);
            setOpen(true);
          }}
          data-space-save-version=""
        >
          <BookmarkIcon aria-hidden className="size-3.5" />
          Save version
        </button>
      </>
    );
  }
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <input
        autoFocus
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && setOpen(false)}
        placeholder="Name this version"
        maxLength={80}
        aria-label="Version name"
        className="h-7 w-44 rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        data-space-version-label=""
      />
      <button type="submit" disabled={busy} className={cn(HEADER_ACTION, "text-foreground")} data-space-version-submit="">
        {busy ? "Saving" : "Save"}
      </button>
      {/* A failed save keeps the form open, so its reason shows here. */}
      {note !== null ? (
        <span className="text-xs text-destructive" role="alert" data-space-version-error="">
          {note}
        </span>
      ) : null}
    </form>
  );
}

/** One space document, open in place of the overview: the editor, its download, versions and history. */
export function SpaceDocumentView({
  spaceId,
  doc,
  row,
  canEdit,
  now,
  onClose,
}: {
  readonly spaceId: string;
  readonly doc: string;
  /** The document's row from the list, once it has loaded. */
  readonly row: SpaceDocumentRow | null;
  readonly canEdit: boolean;
  readonly now: number;
  readonly onClose: () => void;
}) {
  const [history, setHistory] = useState(false);
  const kind = doc === "board" ? "board" : doc.startsWith("sheet:") ? "sheet" : "page";
  const name = row?.name ?? (kind === "board" ? "Board" : kind === "sheet" ? "Sheet" : "Page");
  const versions = row?.versions ?? [];
  return (
    <div data-space-open-document={doc}>
      <div className="mb-6 flex flex-wrap items-center gap-2 text-[13px]">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeftIcon aria-hidden className="size-3.5" />
          Overview
        </button>
        <span aria-hidden className="text-muted-foreground/50">
          /
        </span>
        <span className="min-w-0 truncate font-medium text-foreground" data-space-document-title="">
          {name}
        </span>
        <span className="ms-auto flex items-center gap-1">
          {versions.length > 0 ? (
            <button
              type="button"
              aria-expanded={history}
              onClick={() => setHistory((shown) => !shown)}
              className={HEADER_ACTION}
              data-space-document-history=""
            >
              <HistoryIcon aria-hidden className="size-3.5" />
              {versions.length} {versions.length === 1 ? "version" : "versions"}
            </button>
          ) : null}
          {canEdit ? <SaveVersion spaceId={spaceId} docId={doc} /> : null}
          <DownloadDocument
            spaceId={spaceId}
            docId={doc}
            label={kind === "sheet" ? "Download .xlsx" : "Download .md"}
          />
        </span>
      </div>
      {history && versions.length > 0 ? (
        <VersionList docId={doc} versions={versions} now={now} className="mb-6" />
      ) : null}
      {kind === "board" ? (
        <SpaceBoard spaceId={spaceId} canEdit={canEdit} />
      ) : kind === "page" ? (
        <SpaceBoardLive spaceId={spaceId} docId={doc} canEdit={canEdit} />
      ) : (
        <SpaceSheetLive spaceId={spaceId} docId={doc} />
      )}
    </div>
  );
}
